/**
 * MyCois: the sign-in account's own communities of interest.
 *
 * Drafts (including forked copies of shared COIs) can be resumed in the
 * editor; submitted communities get a Share button for their public link.
 * Rendered inside the /coi page for authenticated users only.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ApiError } from '../../lib/api';
import { listMyCois, type MyCoiItem } from '../../lib/coi';
import { useRequireAuth } from '../../hooks/useRequireAuth';
import { selectIsAuthenticated, useAuthStore } from '../../stores/auth';
import { ShareCoiDialog } from './ShareCoiDialog';

function StatusBadge({ status }: { status: string }): JSX.Element {
  const label = status.replace(/_/g, ' ');
  const classes =
    status === 'draft'
      ? 'bg-amber-100 text-amber-800'
      : status === 'received'
        ? 'bg-tenant-tint text-tenant-strong'
        : status === 'under_review'
          ? 'bg-purple-100 text-purple-800'
          : status === 'accepted'
            ? 'bg-emerald-100 text-emerald-800'
            : status === 'rejected'
              ? 'bg-stone-200 text-stone-600'
              : 'bg-stone-100 text-stone-600';
  return (
    <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium capitalize ${classes}`}>
      {label}
    </span>
  );
}

function CoiRow({
  coi,
  onShare,
  onEdit,
}: {
  coi: MyCoiItem;
  onShare: () => void;
  onEdit: () => void;
}): JSX.Element {
  const isDraft = coi.status === 'draft';
  return (
    <li className="flex items-center gap-3 rounded-lg border border-stone-200 bg-white px-3 py-2.5">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-ink">
          {coi.title ?? 'Untitled community'}
          {coi.parent_coi_id && (
            <span className="ml-2 rounded bg-stone-100 px-1 py-px text-[10px] font-medium text-stone-500">
              copy
            </span>
          )}
        </p>
        <p className="truncate text-xs text-stone-500">{coi.narrative_excerpt}</p>
        <p className="mt-0.5 text-xs text-stone-400">
          <StatusBadge status={coi.status} />
          {coi.has_share_link && <span className="ml-2">🔗 shared</span>}
        </p>
      </div>
      {isDraft ? (
        <button
          type="button"
          onClick={onEdit}
          className="shrink-0 rounded-lg bg-tenant-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-tenant-strong"
        >
          Continue editing
        </button>
      ) : (
        <button
          type="button"
          onClick={onShare}
          className="shrink-0 rounded-lg border border-stone-300 px-3 py-1.5 text-xs font-medium text-stone-700 hover:bg-stone-50"
        >
          Share
        </button>
      )}
    </li>
  );
}

export function MyCois(): JSX.Element | null {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const { requireAuth, signInModal } = useRequireAuth();
  const [shareCoiId, setShareCoiId] = useState<{ id: string; title: string } | null>(null);

  const myCoisQuery = useQuery({
    queryKey: ['my-cois'],
    queryFn: listMyCois,
    retry: false,
    staleTime: 30 * 1000,
    enabled: isAuthenticated,
  });

  if (!isAuthenticated) return null;

  const items = myCoisQuery.data ?? [];
  const drafts = items.filter((c) => c.status === 'draft');
  const submitted = items.filter((c) => c.status !== 'draft');

  function handleEdit(coi: MyCoiItem): void {
    requireAuth(
      () => navigate('/coi', { state: { draftCoiId: coi.id } }),
      'Sign in to continue editing your community.',
    );
  }

  function handleShare(coi: MyCoiItem): void {
    requireAuth(
      () => setShareCoiId({ id: coi.id, title: coi.title ?? 'Untitled community' }),
      'Sign in to share your community.',
    );
  }

  return (
    <section className="mt-10 max-w-2xl" aria-labelledby="my-cois-heading">
      {signInModal}
      <h2 id="my-cois-heading" className="text-base font-semibold text-ink">
        Your communities
      </h2>
      {myCoisQuery.isLoading && (
        <p className="mt-2 text-sm text-stone-500">Loading your communities…</p>
      )}
      {myCoisQuery.error && !(myCoisQuery.error instanceof ApiError && myCoisQuery.error.status === 401) && (
        <p className="mt-2 text-sm text-red-600">
          Could not load your communities. Please try again later.
        </p>
      )}
      {!myCoisQuery.isLoading && !myCoisQuery.error && items.length === 0 && (
        <p className="mt-2 text-sm text-stone-500">
          Nothing here yet — submit a community above and it will appear here, along
          with any copies you make of shared communities.
        </p>
      )}
      {drafts.length > 0 && (
        <div className="mt-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-stone-500">
            Drafts
          </h3>
          <ul className="mt-1 space-y-2">
            {drafts.map((coi) => (
              <CoiRow key={coi.id} coi={coi} onShare={() => handleShare(coi)} onEdit={() => handleEdit(coi)} />
            ))}
          </ul>
        </div>
      )}
      {submitted.length > 0 && (
        <div className="mt-4">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-stone-500">
            Submitted
          </h3>
          <ul className="mt-1 space-y-2">
            {submitted.map((coi) => (
              <CoiRow key={coi.id} coi={coi} onShare={() => handleShare(coi)} onEdit={() => handleEdit(coi)} />
            ))}
          </ul>
        </div>
      )}
      {shareCoiId && (
        <ShareCoiDialog
          coiId={shareCoiId.id}
          coiTitle={shareCoiId.title}
          onClose={() => setShareCoiId(null)}
        />
      )}
    </section>
  );
}
